// src/Paginas/Body.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Container, Row, Col, Card, Badge, Spinner, Alert } from 'react-bootstrap';
import BotonWhatsapp from './BotonWhatsapp';
import FilterChips from './FilterChips';
import { useProductos } from '../hooks/useProductos';
import { useCategorias } from '../hooks/useCategorias';
import { useProductPagination } from '../hooks/useProductPagination';
import LoadMoreButton from './LoadMoreButton';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Convert a category name to a URL-safe slug.
 * e.g. "Navidad" → "navidad", "Día de la Madre" → "dia-de-la-madre"
 */
const toSlug = (name = '') =>
  name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // strip diacritics
    .replace(/[^a-z0-9\s-]/g, '')   // remove non-alphanumeric
    .trim()
    .replace(/\s+/g, '-');

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const Body = () => {
  // ── Data ──────────────────────────────────────────────────────────────────
  const { productos, loading, error } = useProductos();
  const { categorias } = useCategorias();

  // ── Filter state ──────────────────────────────────────────────────────────
  // null = "Todo" (no filter applied)
  const [activeFilter, setActiveFilter] = useState(null);

  // ── Load-more animation state ─────────────────────────────────────────────
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // ── Accessibility: aria-live announcement ─────────────────────────────────
  const [liveMessage, setLiveMessage] = useState('');

  // ── Refs for focus management after Load More ─────────────────────────────
  // cardRefs[i] points to the Col wrapper of visibleProducts[i]
  const cardRefs = useRef([]);

  // ── Derived: filtered product list ────────────────────────────────────────
  const filteredProducts = useMemo(() => {
    if (!activeFilter) return productos;
    return productos.filter((p) => p.CodCategoria === activeFilter);
  }, [productos, activeFilter]);

  // ── Pagination (operates on the filtered list) ────────────────────────────
  const {
    visibleProducts,
    hasMore,
    remainingCount,
    nextLoadCount,
    displayedCount,
    loadMore,
    reset,
  } = useProductPagination(filteredProducts, 12, 8);

  // ── URL → filter on mount (runs once categories are loaded) ───────────────
  useEffect(() => {
    if (categorias.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const tema = params.get('tema');
    if (!tema) return;
    const match = categorias.find((c) => toSlug(c.nomCategoria) === tema);
    if (match) setActiveFilter(match.id);
  }, [categorias]);

  // ── Filter change handler ─────────────────────────────────────────────────
  const handleFilterChange = useCallback(
    (categoryId) => {
      setActiveFilter(categoryId);
      reset(); // go back to initial 12 products

      // Reflect filter in URL without adding a history entry
      if (categoryId) {
        const cat = categorias.find((c) => c.id === categoryId);
        const slug = cat ? toSlug(cat.nomCategoria) : '';
        window.history.replaceState(null, '', `${window.location.pathname}?tema=${slug}`);
      } else {
        window.history.replaceState(null, '', window.location.pathname);
      }
    },
    [categorias, reset]
  );

  // ── Load More handler ─────────────────────────────────────────────────────
  const handleLoadMore = useCallback(() => {
    setIsLoadingMore(true);
    const prevCount = displayedCount; // index of first new card
    const willLoad = nextLoadCount;

    setTimeout(() => {
      loadMore();
      setIsLoadingMore(false);

      // Announce to screen readers
      if (willLoad > 0) {
        setLiveMessage(`Se cargaron ${willLoad} producto${willLoad !== 1 ? 's' : ''} más.`);
      }

      // Move focus to the first newly visible card
      setTimeout(() => {
        const firstNew = cardRefs.current[prevCount];
        if (firstNew) firstNew.focus();
      }, 50);
    }, 300);
  }, [displayedCount, nextLoadCount, loadMore]);

  // ── Price formatter ───────────────────────────────────────────────────────
  const formatPrecio = (precio) => {
    if (!precio) return '₡0';
    return `₡${Number(precio).toLocaleString('es-CR')}`;
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <section
      id="productos"
      className="py-5"
      style={{ backgroundColor: '#f8f5f0' }}
    >
      <Container>

        {/* Encabezado de sección */}
        <div className="text-center mb-5">
          <Badge
            bg="none"
            className="px-4 py-2 mb-3"
            style={{
              backgroundColor: 'rgba(212, 165, 116, 0.15)',
              color: '#d4a574',
              fontSize: '0.8rem',
              fontWeight: '600',
              letterSpacing: '2px',
              textTransform: 'uppercase',
              borderRadius: '50px'
            }}
          >
            Colección Exclusiva
          </Badge>

          <h2
            className="display-5 fw-bold mb-3"
            style={{
              fontFamily: 'Georgia, serif',
              color: '#1a1a1a',
              letterSpacing: '2px'
            }}
          >
            Velas Aromáticas Artesanales
          </h2>

          <div
            style={{
              width: '80px',
              height: '3px',
              backgroundColor: '#d4a574',
              margin: '0 auto',
              borderRadius: '2px'
            }}
          />

          <p
            className="text-muted mt-3"
            style={{
              fontSize: '1.1rem',
              maxWidth: '600px',
              margin: '0 auto'
            }}
          >
            Descubre nuestra colección de velas, creadas con cera de soya
            y aromas que transforman tu hogar.
          </p>
        </div>

        {/* Filter chips — shown once categories are available */}
        {!loading && !error && categorias.length > 0 && (
          <FilterChips
            categorias={categorias}
            activeFilter={activeFilter}
            onFilterChange={handleFilterChange}
          />
        )}

        {/* Loading state */}
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: '#d4a574' }} />
            <p className="mt-3 text-muted">Cargando productos...</p>
          </div>
        )}

        {/* Error state */}
        {error && (
          <Alert variant="danger" className="text-center">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Error al cargar los productos: {error}
          </Alert>
        )}

        {/* Empty catalog */}
        {!loading && !error && productos.length === 0 && (
          <div className="text-center py-5">
            <p className="text-muted" style={{ fontSize: '1.1rem' }}>
              No hay productos disponibles en este momento.
            </p>
          </div>
        )}

        {/* Empty filtered result */}
        {!loading && !error && productos.length > 0 && filteredProducts.length === 0 && (
          <div className="text-center py-5">
            <p className="text-muted" style={{ fontSize: '1.1rem' }}>
              No hay productos en esta categoría.
            </p>
          </div>
        )}

        {/* Product grid */}
        {!loading && !error && visibleProducts.length > 0 && (
          <>
            <Row className="g-4">
              {visibleProducts.map((producto, index) => (
                <Col
                  key={producto.id}
                  xs={12}
                  sm={6}
                  lg={4}
                  xl={3}
                  // tabIndex={-1} allows programmatic focus after Load More
                  tabIndex={-1}
                  ref={(el) => { cardRefs.current[index] = el; }}
                  data-aos="fade-up"
                  data-aos-duration="600"
                  data-aos-delay={index < 12 ? index * 50 : 0}
                  data-aos-once="true"
                  style={{ outline: 'none' }} // suppress focus ring on the Col wrapper
                >
                  <Card
                    className="h-100 border-0 shadow-sm"
                    style={{
                      transition:
                        'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                      backgroundColor: 'white',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-8px)';
                      e.currentTarget.style.boxShadow =
                        '0 20px 40px rgba(0,0,0,0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow =
                        '0 4px 15px rgba(0,0,0,0.05)';
                    }}
                  >
                    <div
                      className="position-relative"
                      style={{
                        height: '250px',
                        overflow: 'hidden',
                        backgroundColor: '#f8f5f0'
                      }}
                    >
                      <Card.Img
                        variant="top"
                        src={producto.imagen}
                        alt={`${producto.nombre} - Lumière Tica`}
                        // Eager-load the first 8 images (above the fold on desktop).
                        // Lazy-load everything beyond that to improve LCP and save bandwidth.
                        loading={index < 8 ? 'eager' : 'lazy'}
                        // Explicit dimensions prevent layout shift (CLS).
                        width="400"
                        height="250"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transition: 'transform 0.6s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                      />
                    </div>

                    <Card.Body className="p-4 d-flex flex-column">

                      <Card.Title
                        as="h3"
                        className="mb-2"
                        style={{
                          fontFamily: 'Georgia, serif',
                          fontSize: '1.1rem',
                          fontWeight: '700',
                          color: '#1a1a1a',
                          letterSpacing: '0.5px'
                        }}
                      >
                        {producto.nombre}
                      </Card.Title>

                      <Card.Text
                        className="flex-grow-1"
                        style={{
                          fontSize: '0.9rem',
                          color: '#666',
                          lineHeight: '1.6',
                          marginBottom: '1rem'
                        }}
                      >
                        {producto.descripcion}
                      </Card.Text>

                      <div
                        className="d-flex align-items-center justify-content-between mt-auto pt-3 border-top"
                        style={{ borderColor: '#f0ebe5' }}
                      >
                        <span
                          className="fw-bold"
                          style={{
                            fontSize: '1.3rem',
                            color: '#1a1a1a',
                            fontFamily: 'Georgia, serif'
                          }}
                        >
                          {formatPrecio(producto.precio)}
                        </span>

                        <BotonWhatsapp
                          producto={producto}
                          className="px-4 py-2"
                          style={{
                            fontSize: '0.85rem'
                          }}
                        >
                          <i
                            className="fab fa-whatsapp"
                            style={{ fontSize: '1.1rem' }}
                          ></i>
                          Comprar
                        </BotonWhatsapp>
                      </div>

                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Load More Button */}
            {hasMore && (
              <LoadMoreButton
                onClick={handleLoadMore}
                remainingCount={remainingCount}
                nextLoadCount={nextLoadCount}
                loading={isLoadingMore}
              />
            )}
          </>
        )}

        {/* Screen-reader live region — announces how many products were loaded */}
        <div aria-live="polite" className="visually-hidden">
          {liveMessage}
        </div>

        {/* Sección de personalización */}
        <Row className="mt-5">
          <Col lg={12}>
            <div
              className="p-5 rounded-4 text-center"
              style={{
                background:
                  'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: '-50%',
                  right: '-10%',
                  width: '50%',
                  height: '200%',
                  background:
                    'radial-gradient(circle, rgba(212, 165, 116, 0.05) 0%, transparent 70%)',
                  pointerEvents: 'none'
                }}
              />

              <h2
                className="text-white mb-3"
                style={{
                  fontFamily: 'Georgia, serif',
                  fontSize: 'clamp(1.5rem, 3vw, 2.5rem)',
                  letterSpacing: '2px',
                  position: 'relative',
                  zIndex: 1
                }}
              >
                ¿Buscas una vela personalizada?
              </h2>

              <p
                className="text-white-50 mb-4"
                style={{
                  fontSize: '1.1rem',
                  maxWidth: '500px',
                  margin: '0 auto',
                  position: 'relative',
                  zIndex: 1
                }}
              >
                Contáctanos y creamos una vela personalizada con el aroma
                que prefieras, ideal para regalos y ocasiones especiales.
              </p>

              <BotonWhatsapp
                personalizado={true}
                className="px-5 py-3"
                style={{
                  fontSize: '1rem',
                  boxShadow: '0 4px 20px rgba(37, 211, 102, 0.3)',
                  position: 'relative',
                  zIndex: 1
                }}
              >
                <i className="fab fa-whatsapp me-2"></i>
                Pedir Vela Personalizada
              </BotonWhatsapp>
            </div>
          </Col>
        </Row>

      </Container>
    </section>
  );
};

export default Body;
